import React from "react";
import { useParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { LessonOrUnitGroup, LessonOrUnitGroupKind } from "../common/api";
import { LessonItem, LessonItemLoggingContext } from "../components/LessonItem";
import { ApiContext } from "../common/context/ApiContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { useLiveData } from "../common/utils";
import { BackNavRow, PageContainer } from "../common/CommonStyles";
import { Account, AccountContext } from "../common/context/AccountContext";
import { default as UUID } from "uuid";
import { useTranslation } from "react-i18next";
import { LeftCornerDecoration } from "../components/LessonItemBody";
import {
  AnalyticsContext,
  ViewStudentAssignmentsEvent,
} from "../common/context/AnalyticsContext";

type StudentAssignmentsState = {
  groups: [LessonOrUnitGroup] | null;
};

export const StudentAssignmentsPage: React.FC = () => {
  const [state, setState] = React.useState<StudentAssignmentsState>({
    groups: null,
  });
  const { teacherId }: { teacherId: string } = useParams<{
    teacherId: string;
  }>();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { accountLiveData } = React.useContext(AccountContext);
  const assignmentsFetchedForProfileStateVersion = React.useRef<number | null>(
    null
  );
  const fetchJobForProfileStateVersion = React.useRef<number | null>(null);
  const currentFetchJobUuid = React.useRef<string | null>(null);
  const timeoutId = React.useRef<number>();
  const { getStudentAssignmentsMetadata } = React.useContext(ApiContext);
  const [account, setAccount] = React.useState<Account>();
  const { t } = useTranslation();
  const { logEvent } = React.useContext(AnalyticsContext);

  React.useEffect(() => {
    logEvent(new ViewStudentAssignmentsEvent());
  }, [logEvent]);

  const maybeRefetchStudentAssignments = React.useCallback(
    async (account: Account) => {
      if (!teacherId) {
        return;
      }
      if (account.hasMaybeProfileState()) {
        setAccount(account);
        if (
          account.profileStateCurrentVersion !==
            assignmentsFetchedForProfileStateVersion.current &&
          account.profileStateCurrentVersion !==
            fetchJobForProfileStateVersion.current
        ) {
          startLoading();
          const fetchJobUuid = (currentFetchJobUuid.current = UUID.v4());
          fetchJobForProfileStateVersion.current =
            account.profileStateCurrentVersion;
          const assignmentMetadata = await getStudentAssignmentsMetadata(
            account.profile?.id || null,
            +teacherId,
            account.profileState?.lessons_in_progress || {}
          );
          if (currentFetchJobUuid.current !== fetchJobUuid) {
            return;
          }
          setState({ groups: assignmentMetadata.groups });
          assignmentsFetchedForProfileStateVersion.current =
            fetchJobForProfileStateVersion.current;
          stopLoading();
          timeoutId.current = window.setTimeout(() => {
            currentFetchJobUuid.current = UUID.v4();
            assignmentsFetchedForProfileStateVersion.current = null;
            fetchJobForProfileStateVersion.current = null;
            maybeRefetchStudentAssignments(accountLiveData.value);
          }, 8 * 60 * 60 * 1000);
        }
      }
    },
    [
      accountLiveData,
      teacherId,
      getStudentAssignmentsMetadata,
      startLoading,
      stopLoading,
    ]
  );

  useLiveData(
    React.useCallback(
      (account: Account) => {
        maybeRefetchStudentAssignments(account);
      },
      [maybeRefetchStudentAssignments]
    ),
    accountLiveData
  );

  if (!state.groups || !account) {
    return <></>;
  }

  return (
    <PageContainer>
      <BackNavRow label={t("generic_nav_back")}>
        <></>
      </BackNavRow>
      {state.groups.map((group) => {
        if (
          group.group === LessonOrUnitGroupKind.StudentAssignedLessons &&
          group.lessons!.length > 0
        ) {
          return (
            <Row key={group.title}>
              <h2 className="mb-3">{group.title}</h2>
              <p className="mb-3">{group.description}</p>
              {group.lessons!.map((lesson) => (
                <Col
                  key={`${lesson.id}-${lesson.custom_flashpack_id}`}
                  xs={12}
                  lg={6}
                  className="mb-3"
                >
                  <LessonItem
                    lesson={lesson}
                    unitId={null}
                    account={account}
                    leftCornerDecoration={LeftCornerDecoration.STANDARD}
                    loggingContext={LessonItemLoggingContext.StudentAssigned}
                  />
                </Col>
              ))}
            </Row>
          );
        }
        return null;
      })}
    </PageContainer>
  );
};
